import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark, Loader2, Pause, Play, RefreshCw, ShieldCheck } from 'lucide-react';
import { CategoryIcon } from '@/components/ui/CategoryIcon';
import { institutionBrandSlug, institutionBrand } from '@/features/open-finance/institution-brand';
import { updateOpenFinanceScheduleBodySchema, type OpenFinanceSettingsResponse, type SyncOpenFinanceResponse } from '@lira/shared';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { boundedSyncProgress } from '@/features/open-finance/review-queue';

function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-[var(--radius-panel)] bg-surface p-5 shadow-card sm:p-6 ${className}`}>{children}</div>;
}

type Connection = OpenFinanceSettingsResponse['connections'][number];
type Source = Connection['sources'][number];
const QUERY_KEY = ['open-finance-settings'] as const;

export function collectSyncTimes(times: string[]): string[] {
  return updateOpenFinanceScheduleBodySchema.parse({ timezone: 'America/Sao_Paulo', syncTimes: times }).syncTimes.slice().sort();
}

export function connectionStatusLabel(status: Connection['status']): string {
  return ({ active: 'Ativa', pending_auth: 'Aguardando autorização', refreshing: 'Sincronizando',
    attention_required: 'Atenção necessária', consent_expired: 'Consentimento expirado', disconnected: 'Desconectada' })[status];
}

function lastSyncLabel(value: string | null): string {
  if (!value) return 'Ainda não sincronizada';
  return `Última sincronização: ${new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(value))}`;
}

function ConnectionCard({ connection, onSaveSchedule, onToggleSource, saving, toggling }: {
  connection: Connection;
  onSaveSchedule: (id: string, timezone: string, times: string[]) => void;
  onToggleSource: (source: Source) => void;
  saving: boolean;
  toggling: boolean;
}) {
  const [times, setTimes] = useState(connection.syncTimes);
  const [editing, setEditing] = useState(false);
  const brandSlug = institutionBrandSlug(connection.institutionName);
  const brand = brandSlug ? institutionBrand(connection.institutionName) : undefined;
  const save = () => {
    try {
      const sorted = collectSyncTimes(times);
      onSaveSchedule(connection.id, connection.timezone, sorted);
    } catch { toast('Revise os horários: use HH:mm sem repetições.', 'error'); }
  };
  return (
    <Card className="min-w-0 space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-ink">{brand && brandSlug
            ? <CategoryIcon icon={`brand:${brandSlug}`} color={brand.hex} size={32} />
            : <Landmark className="size-5 shrink-0 text-primary" />}{connection.institutionName}</h3>
          <p className="mt-1 text-sm text-muted">{lastSyncLabel(connection.lastSuccessfulSyncAt)}</p>
        </div>
        <span className="rounded-full bg-primary-weak px-3 py-1 text-xs font-semibold text-primary">{connectionStatusLabel(connection.status)}</span>
      </header>
      <div className="space-y-3">
        {connection.sources.map((source) => (
          <div key={source.id} className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3">
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{source.displayName}</p>
              <p className="text-xs text-muted">Conta no Lira: {source.mappedName ?? 'Sem vínculo'} · {source.status === 'active' ? 'Sincronização ativa' : 'Sincronização pausada'}</p>
            </div>
            {(source.status === 'active' || source.status === 'paused') && <button type="button" disabled={toggling || (source.status === 'paused' && connection.status !== 'active')}
              onClick={() => onToggleSource(source)}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-semibold text-ink hover:bg-canvas disabled:opacity-50">
              {source.status === 'active' ? <><Pause className="size-4" />Pausar</> : <><Play className="size-4" />Retomar</>}
            </button>}
          </div>
        ))}
      </div>
      <div className="border-t border-border pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h4 className="font-semibold text-ink">Sincronização automática</h4>
            <p className="text-sm text-muted">{connection.syncTimes.join(' · ')} · {connection.timezone.replace('_', ' ')}</p></div>
          <button type="button" onClick={() => { setTimes(connection.syncTimes); setEditing(!editing); }}
            className="h-11 rounded-full border border-border px-4 text-sm font-semibold text-ink hover:bg-canvas">{editing ? 'Cancelar' : 'Alterar horários'}</button>
        </div>
        {editing && <div className="mt-4 space-y-4 rounded-lg bg-canvas p-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {times.map((time, index) => <label key={index} className="min-w-0 text-sm font-medium text-ink">Horário {index + 1}
              <input type="time" value={time} onChange={(event) => setTimes((current) => current.map((value, i) => i === index ? event.target.value : value))}
                className="mt-1 block h-11 w-full min-w-0 rounded-lg border border-border bg-surface px-2 text-ink" />
            </label>)}
          </div>
          <div className="flex flex-wrap gap-2">
            {times.length < 4 && <button type="button" onClick={() => setTimes((current) => [...current, '12:00'])} className="h-11 rounded-full border border-border px-4 text-sm font-medium text-ink">Adicionar horário</button>}
            {times.length > 1 && <button type="button" onClick={() => setTimes((current) => current.slice(0, -1))} className="h-11 rounded-full border border-border px-4 text-sm font-medium text-ink">Remover último</button>}
            <button type="button" disabled={saving} onClick={save} className="h-11 rounded-full bg-primary px-5 text-sm font-semibold text-white disabled:opacity-50">{saving ? 'Salvando…' : 'Salvar horários'}</button>
          </div>
        </div>}
      </div>
    </Card>
  );
}

/** One tenant's Belvo controls; never surfaces server credentials or another user's connections. */
export function OpenFinanceSettingsTab() {
  const queryClient = useQueryClient();
  const [syncStartedAt, setSyncStartedAt] = useState<number | null>(null);
  const [syncProgress, setSyncProgress] = useState(0);
  useEffect(() => {
    if (syncStartedAt === null) return;
    const interval = window.setInterval(() => setSyncProgress(boundedSyncProgress(Date.now() - syncStartedAt)), 300);
    return () => window.clearInterval(interval);
  }, [syncStartedAt]);
  const settings = useQuery({ queryKey: QUERY_KEY, queryFn: () => apiGet<OpenFinanceSettingsResponse>('/open-finance/settings') });
  const schedule = useMutation({
    mutationFn: ({ id, timezone, syncTimes }: { id: string; timezone: string; syncTimes: string[] }) =>
      apiPatch<OpenFinanceSettingsResponse>(`/open-finance/connections/${id}/schedule`, { timezone, syncTimes }),
    onSuccess: (data) => { queryClient.setQueryData(QUERY_KEY, data); toast('Horários atualizados.', 'success'); },
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const sourceStatus = useMutation({
    mutationFn: (source: Source) => apiPatch<OpenFinanceSettingsResponse>(`/open-finance/sources/${source.id}`, { status: source.status === 'active' ? 'paused' : 'active' }),
    onSuccess: (data) => { queryClient.setQueryData(QUERY_KEY, data); toast('Configuração da conta atualizada.', 'success'); },
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const sync = useMutation({
    mutationFn: () => apiPost<SyncOpenFinanceResponse>('/open-finance/sync', {}),
    onMutate: () => { setSyncStartedAt(Date.now()); setSyncProgress(0); },
    onSuccess: (response) => {
      setSyncProgress(100);
      const failed = response.runs.filter((run) => run.status !== 'succeeded');
      const created = response.runs.reduce((total, run) => total + run.reviewItemsCreated, 0);
      toast(failed.length ? 'Sincronização parcial: confira a fila.' : `${created} movimentação(ões) nova(s) para revisão.`, failed.length ? 'error' : 'success');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['open-finance', 'reviews'] });
    },
    onError: (error) => toast(errorMessage(error), 'error'),
    onSettled: () => setSyncStartedAt(null),
  });
  return <div className="min-w-0 space-y-5">
    <Card className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-lg font-semibold text-ink">Belvo · Open Finance</h2>
          <p className="mt-1 text-sm text-muted">Controle suas conexões e quando buscar novas movimentações. Nenhum lançamento é criado sem aprovação.</p></div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={sync.isPending || !settings.data?.connections.length} onClick={() => sync.mutate()}
            className="relative inline-flex h-11 items-center gap-2 overflow-hidden rounded-full border border-border px-4 text-sm font-semibold text-ink hover:bg-canvas disabled:opacity-50">
            {sync.isPending && <span aria-hidden className="absolute inset-y-0 left-0 bg-primary-weak transition-[width] duration-300" style={{ width: `${syncProgress}%` }} />}
            <span className="relative inline-flex items-center gap-2">{sync.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
              {sync.isPending ? `Sincronizando · ${syncProgress}%` : 'Sincronizar agora'}</span>
          </button>
          <Link to="/movimentacoes/reviews" className="inline-flex h-11 items-center rounded-full bg-primary px-4 text-sm font-semibold text-white">Abrir fila de revisão</Link>
        </div>
      </div>
      {settings.data?.provider.pilotActive && <p className="mt-4 flex items-start gap-2 rounded-lg bg-primary-weak p-3 text-sm text-ink"><ShieldCheck className="mt-0.5 size-4 shrink-0 text-primary" />Seu piloto está ativo para as contas listadas abaixo.</p>}
      {settings.data && !settings.data.provider.newConnectionsAvailable && <p className="mt-4 rounded-lg border border-border p-3 text-sm text-muted">
        Novas autorizações bancárias para outros usuários ainda não estão disponíveis: a aplicação precisa de credenciais e habilitação próprias de produção do Belvo. A conexão pessoal existente não será compartilhada.
      </p>}
    </Card>
    {settings.isLoading && <Card><p role="status" className="text-sm text-muted">Carregando conexões…</p></Card>}
    {settings.isError && <Card><p role="alert" className="text-sm text-expense">{errorMessage(settings.error)}</p>
      <button type="button" onClick={() => void settings.refetch()} className="mt-3 h-11 rounded-full border border-border px-4 text-sm font-medium">Tentar novamente</button></Card>}
    {settings.data?.connections.length === 0 && <Card><p className="text-sm text-muted">Você ainda não tem instituições conectadas nesta conta.</p></Card>}
    {settings.data?.connections.map((connection) => <ConnectionCard key={connection.id} connection={connection}
      onSaveSchedule={(id, timezone, syncTimes) => schedule.mutate({ id, timezone, syncTimes })}
      onToggleSource={(source) => sourceStatus.mutate(source)} saving={schedule.isPending} toggling={sourceStatus.isPending} />)}
  </div>;
}
